import API from '@API/index';
import LoadingContent from '@components/overview/LoadingContent';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Step as MUIStep,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import React from 'react';
import styled from 'styled-components';
import ReactDiffViewer from 'react-diff-viewer';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import CodeMirror from '@uiw/react-codemirror';
import { json, jsonLanguage } from '@codemirror/lang-json';
import Utils from '@utils/index';

const Container = styled(Box)`
  .MuiOutlinedInput-notchedOutline {
    height: unset;
  }

  .MuiInputBase-root {
    height: unset;
  }
`

type StreamsCVParametersEditorProps = {
  streams: VideoStream[];
  onClose?: () => void;
}

type Field = {
  id: number;
  property: string;
  value?: string;
}

type Operation = "add_update" | "remove" | "override" | undefined;

const StreamsCVParametersEditor: React.FC<StreamsCVParametersEditorProps> = (props) => {
  const { streams, onClose } = props;

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [isSaving, setSaving] = React.useState<boolean>(false);
  const [currentStepNumber, setCurrentStepNumber] = React.useState<number>(1);
  const [operation, setOperation] = React.useState<Operation>(undefined);
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [fields, setFields] = React.useState<Field[]>([])
  const [originalStreams, setOriginalStreams] = React.useState<VideoStream[]>([]);
  const [modifiedStream, setModifiedStream] = React.useState<VideoStream[]>([]);
  const [selectedTab, setSelectedTab] = React.useState<string>("");
  const [rawJson, setRawJson] = React.useState<string | undefined>(undefined);
  const [jsonError, setJsonError] = React.useState<undefined | string>(undefined);

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const rawJsonRef = React.useRef<string | undefined>(rawJson);

  const selectedStream = React.useMemo(() => {
    return originalStreams.find(el => el.kvsName === selectedTab);
  }, [originalStreams, selectedTab])

  const isNextDisabled = React.useMemo(() => {
    if (currentStepNumber === 2 && operation === "override" && jsonError !== undefined) return true;
    if (currentStepNumber === 2 && operation === "override" && jsonError === undefined && rawJson !== undefined) return false;
    if (currentStepNumber === 2 && fields.length === 0) return true;
    if (fields.find((e) => isEmpty(e.property))) return true;
    if (fields.find((e) => isEmpty(e.value))) return true;
    let duplicate: boolean = false;

    for (let i = 0; i < fields.length; i += 1) {
      duplicate = fields.filter(el => el.property === fields[i].property).length > 1
      if (duplicate) break;
    }

    return duplicate;
  }, [currentStepNumber, fields, jsonError, operation, rawJson]);

  React.useEffect(() => {
    rawJsonRef.current = rawJson;
  }, [rawJson])

  React.useEffect(() => {
    setLoading(true);
    console.log('Loading CV parameters...');
    console.log(streams);

    const sites: Set<string> = new Set();
    streams.forEach((stream) => sites.add(stream.siteId));

    const fullStreams: VideoStream[] = [];

    Promise.all(Array.from(sites).map((siteId) => API.Streams.listStreams(siteId)))
      .then((siteStreams) => {
        siteStreams.forEach(siteStream => {
          siteStream.forEach((e) => {
            const target = streams.find(el => el.kvsName === e.kvsName);
            if (target) {
              fullStreams.push(e);
            }
          })
        })

        const cvparameters = fullStreams.map(el => el.cvParameters);

        const suggest: Set<string> = new Set();

        cvparameters.forEach((e) => {
          const keys = Object.keys(e);
          keys.forEach((k) => suggest.add(k));
        })

        setSuggestions(Array.from(suggest))

        console.log(cvparameters);
        setOriginalStreams(fullStreams);
      })
      .finally(() => {
        setLoading(false);
      })
  }, [streams]);

  const save = React.useCallback(() => {
    setSaving(true);
    const promises = modifiedStream.map((stream) => API.Streams.updateStream(stream));
    Promise.allSettled(promises)
      .then((promises) => {
        const fullfiled = promises.filter(el => el.status === "fulfilled").length;

        enqueueSnackbar(`${fullfiled} of ${promises.length} streams saved!`, { variant: fullfiled === promises.length ? 'success' : 'error' })
        if (onClose) onClose();
      })
      .catch((e) => {

      })
  }, [enqueueSnackbar, modifiedStream, onClose])

  const generate = React.useCallback((originalStreams: VideoStream[], fields: Field[], operation: Operation) => {
    if (operation) {
      if (operation === "add_update") {
        const changedStreams = JSON.parse(JSON.stringify(originalStreams)) as VideoStream[];

        changedStreams.forEach((stream) => {
          fields.forEach((field) => {
            //@ts-ignore
            stream.cvParameters[field.property] = field.value
          })
        })

        setSelectedTab(changedStreams[0]?.kvsName)
        setModifiedStream(changedStreams);
      } else if (operation === "remove") {
        const changedStreams = JSON.parse(JSON.stringify(originalStreams)) as VideoStream[];

        changedStreams.forEach((stream) => {
          fields.forEach((field) => {
            if (Object.keys(stream.cvParameters).includes(field.property)) {
              //@ts-ignore
              delete stream.cvParameters[field.property];
            }
          })
        })

        setSelectedTab(changedStreams[0]?.kvsName)
        setModifiedStream(changedStreams);
      } else if (operation === "override") {
        const changedStreams = JSON.parse(JSON.stringify(originalStreams)) as VideoStream[];

        changedStreams.forEach((stream) => {
          if (rawJsonRef.current) stream.cvParameters = rawJsonRef.current;
        })

        setSelectedTab(changedStreams[0]?.kvsName)
        setModifiedStream(changedStreams);
      }
      console.log(originalStreams);
      console.log(operation);
      console.log(fields);
    }
  }, [])

  const onJsonChange = React.useCallback((jsonParams: any) => {
    setJsonError(undefined)
    try {
      setRawJson(JSON.parse(jsonParams))
    } catch (error) {
      setJsonError(Utils.Error.getErrorMessage(error))
    }
  }, [])

  return (
    <Dialog
      open={true}
      onClose={onClose}
    >
      <Box
        padding="1.5rem"
        width="80vw"
        height="80vh"
        display="grid"
        gridTemplateRows="auto max-content"
      >
        {loading === true &&
          <LoadingContent message="Loading CV parameters..." />
        }
        {isSaving === true &&
          <LoadingContent message="Saving CV changes..." />
        }
        {loading === false && isSaving === false &&
          <Box
            width="100%"
            height="100%"
            display="grid"
            gridTemplateRows="max-content auto max-content"
            gap='1rem'
          >
            <Box display="grid" gridTemplateRows="max-content max-content" gap="1rem" padding="0.5rem">
              <Typography variant="h5">Edit Camera CV Parameters</Typography>
              <Stepper activeStep={currentStepNumber - 1}>
                <MUIStep completed={currentStepNumber > 1}>
                  <StepLabel style={{ maxWidth: "8rem" }}>Choose your operation</StepLabel>
                </MUIStep>
                <MUIStep style={{ maxWidth: "9rem" }} completed={currentStepNumber > 2}>
                  <StepLabel>Prepare the update</StepLabel>
                </MUIStep>
                <MUIStep style={{ maxWidth: "12rem" }} completed={currentStepNumber > 3}>
                  <StepLabel>Review</StepLabel>
                </MUIStep>
              </Stepper>
            </Box>
            <Box>
              {currentStepNumber === 1 &&
                <Box display="grid" width="10rem" gridTemplateRows="max-content max-content max-content" gap="1rem">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setOperation("add_update");
                      setCurrentStepNumber((curr) => curr + 1)
                    }}
                  >
                    Update
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setOperation("remove");
                      setCurrentStepNumber((curr) => curr + 1)
                    }}
                  >
                    Remove
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setOperation("override");
                      setCurrentStepNumber((curr) => curr + 1)
                    }}
                  >
                    Override
                  </Button>
                </Box>
              }
              {currentStepNumber === 2 && (operation === "add_update" || operation === "remove") &&
                <Container display="grid" gridAutoRows="max-content" gap="1rem">
                  {fields.map((el) => (
                    <Box display="grid" gridTemplateColumns="max-content max-content" gap="1rem">
                      <Autocomplete
                        freeSolo
                        options={suggestions}
                        value={el.property}
                        style={{ width: '20rem' }}
                        key={`${el.id}-prop`}
                        onChange={(_e, r, _d) => {
                          setFields((curr) => {
                            const n = Array.from(curr);
                            const t = curr.find(e => e.id === el.id);
                            if (t && r) t.property = r
                            return n;
                          })
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label={"Property"}
                            value={el.property}
                            error={isEmpty(el.property) || fields.filter(e => e.property === el.property).length > 1}
                            helperText={isEmpty(el.property) ? "Property cannot be empty" : fields.filter(e => e.property === el.property).length > 1 ? "Duplicaded properties" : undefined}
                            onChange={(e) => {
                              setFields((curr) => {
                                const n = Array.from(curr);
                                const t = curr.find(e => e.id === el.id);
                                if (t) t.property = e?.currentTarget?.value || e?.target?.value || t?.property || ""
                                return n;
                              })
                            }}
                          />
                        )}
                      />
                      {operation !== "remove" &&
                        <Autocomplete
                          freeSolo
                          options={[]}
                          value={el.value}
                          style={{ width: '20rem' }}
                          key={`${el.id}-val`}
                          onChange={(e) => {

                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label={"Value"}
                              value={el.value}
                              error={isEmpty(el.value)}
                              helperText={isEmpty(el.value) ? "Value cannot be empty" : undefined}
                              onChange={(e) => {
                                setFields((curr) => {
                                  const n = Array.from(curr);
                                  try {
                                    const t = curr.find(c => c.id === el.id);
                                    if (t) t.value = e?.currentTarget?.value || e?.target?.value || t?.value || ""
                                    return n;
                                  } catch (e) {
                                    console.log(e);
                                    return n;
                                  }
                                })
                              }}
                            />
                          )}
                        />
                      }
                    </Box>
                  ))}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setFields((curr) => [...curr, { property: "", value: "", id: Date.now() }])}
                    fullWidth={false}
                    style={{ width: '10rem' }}
                  >
                    Add property
                  </Button>
                </Container>
              }
              {currentStepNumber === 2 && operation === "override" &&
                <Container display="grid" gridAutoRows="max-content" gap="1rem">
                  <div ref={containerRef} style={{ width: '100%' }}>
                    <CodeMirror
                      value={JSON.stringify(rawJson, null, 2)}
                      height="100%"
                      extensions={[json(), jsonLanguage]}
                      minHeight='50vh'
                      maxHeight='50vh'
                      width={containerRef.current ? `${containerRef.current.offsetWidth}px` : "77.5vw"}
                      maxWidth={containerRef.current ? `${containerRef.current.offsetWidth}px` : "77.5vw"}
                      onChange={onJsonChange}
                    />
                  </div>
                  {jsonError && <Typography color="red">{jsonError}</Typography>}
                </Container>
              }
              {currentStepNumber === 3 &&
                <Box>
                  <Tabs value={selectedTab} onChange={(_e, v) => setSelectedTab(v)}>
                    {
                      originalStreams.map((stream) => (
                        <Tab
                          label={stream.defaultName || stream.kvsName}
                          value={stream.kvsName}
                        />
                      ))
                    }
                  </Tabs>
                  <Box padding="1rem" overflow="auto" height="50vh">
                    <Typography variant="subtitle1">{selectedStream?.defaultName || selectedStream?.kvsName}</Typography>
                    <ReactDiffViewer
                      oldValue={JSON.stringify(selectedStream?.cvParameters, null, 2)}
                      newValue={JSON.stringify(modifiedStream.find(el => el.kvsName === selectedStream?.kvsName)?.cvParameters, null, 2)}
                      showDiffOnly
                      splitView={false}
                    />
                  </Box>
                </Box>
              }
            </Box>
            <Box padding="0.5rem" display="grid" gridTemplateColumns="auto max-content max-content" gap="1rem" width="100%">
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onClose}
                  data-cy="edit-camera-cv-parameters-cancel"
                >
                  Cancel
                </Button>
              </Box>
              <Button
                disabled={currentStepNumber === 1}
                variant="outlined"
                color="secondary"
                onClick={() => { setCurrentStepNumber((curr) => curr - 1) }}
                data-cy="create-action-dialog-move-to-previous-step"
              >
                Back
              </Button>
              {currentStepNumber !== 3 &&
                <Button
                  disabled={isNextDisabled}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (currentStepNumber === 2) generate(originalStreams, fields, operation);
                    setCurrentStepNumber((curr) => curr + 1)
                  }}
                  data-cy="create-action-dialog-move-to-next-step"
                >
                  Next
                </Button>
              }
              {currentStepNumber === 3 &&
                <Button
                  disabled={isSaving}
                  variant="contained"
                  color="primary"
                  onClick={save}
                  style={{ width: '5rem' }}
                  data-cy="create-action-dialog-save"
                >
                  Save
                </Button>
              }
            </Box>
          </Box>
        }
      </Box>
    </Dialog>
  )
}

export default StreamsCVParametersEditor;
